import { AppLink } from '@/shared/components/AppLink';
import { resolveImageUrl } from '@/shared/utils/imageUtils';
import { useIsMobile } from '@/core/hooks/useMediaQuery';

interface RoundIconItem {
  title: string;
  imageUrl: string;
  link?: string;
  lazyload?: string;
  shouldRefresh?: boolean;
}

interface RoundIconSectionProps {
  data: Record<string, unknown> & {
    title?: string | string[];
    titleImageUrl?: string;
    dTitleImageUrl?: string;
    items?: RoundIconItem[];
    blankImageUrl?: string;
  };
  sectionKey: string;
}

export default function RoundIconSection({
  data,
  sectionKey,
}: RoundIconSectionProps) {
  const items = (data.items || []) as RoundIconItem[];
  const title = Array.isArray(data.title) ? data.title.join(' ') : (data.title as string | undefined);
  const mTitleImageUrl = data.titleImageUrl as string | undefined;
  const dTitleImageUrl = data.dTitleImageUrl as string | undefined;
  const isMobile = useIsMobile();
  const titleImageUrl = isMobile ? mTitleImageUrl : dTitleImageUrl;

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <section className="py-8 md:py-12 md:bg-[#FFF8F9] md:overflow-x-hidden">
      <div className="container-fluid">
        <div
          className={[
            'md:flex md:items-center transform-gpu',
            'transition-transform duration-1000 ease-out motion-reduce:transition-none motion-reduce:transform-none',
            'md:translate-x-1/2 md:group-data-[inview=true]:translate-x-0',
          ].join(' ')}
        >
          {/* Section Title */}
          {titleImageUrl && (
            <div className="mb-6 md:mb-8 md:w-1/2 text-center md:text-right">
              <img
                className="inline-block max-w-[110px] max-h-[38px] md:w-[80%] md:max-w-none md:max-h-none"
                src={resolveImageUrl(titleImageUrl)}
                alt={title || ''}
                loading="lazy"
                decoding="async"
              />
            </div>
          )}

          {/* Round Icon Grid */}
          <div className="grid grid-cols-3 gap-4 md:gap-6 justify-items-center border border-[#06234E] rounded-[16px] p-4 md:w-1/2">
            {items.map((item, index) => {
              const hasLink = item.link && item.link.trim() !== '';
              const iconContent = (
                <>
                  <img
                    src={resolveImageUrl(item.imageUrl)}
                    alt={item.title}
                    className="w-[65px] h-[65px] object-contain"
                    loading={item.lazyload ? 'lazy' : 'eager'}
                    decoding="async"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                  <h3 className="text-center mt-2 font-secondary text-[11px] leading-[14px] md:text-base text-[#032C5E] font-normal">
                    {item.title}
                  </h3>
                </>
              );

              return (
                <div key={`round-icon-${item.title}-${index}`} className="flex flex-col items-center">
                  {hasLink && item.link ? (
                    <AppLink
                      to={item.link}
                      className="flex flex-col items-center hover:opacity-80 transition-opacity"
                      data-eventname={`${sectionKey}_${item.title.replace(/\s/g, '')}`}
                      shouldRefresh={item.shouldRefresh}
                    >
                      {iconContent}
                    </AppLink>
                  ) : (
                    iconContent
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

